import { FC, useEffect, useCallback, useRef, useState, ChangeEvent } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import IconButton from '@mui/material/IconButton';
import ErrorIcon from '@mui/icons-material/Error';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import { CSSProperties } from '@mui/styles';
import { callUserLogin } from '../../store/api/auth-api';
import { useRecaptchaRequired } from '../../utils/use-recaptcha-required';
import { PanelInfoAppTheme } from '../../survey-tool/declarations/ah-api';
import { validateEmail } from '../../utils/email';
import { isClientErr } from '../../store/api/api-call';
import { RECAPTCHA_SITE_KEY, RECAPTCHA_SITE_NAME } from '../../app-config';
import { storeToken } from '../../auth-token';
import { checkAndUseQueryRedirect, redirectIframeSafe } from '../../utils/uri';
import { useAppSelector } from '../../store';
import { PPButton } from '../../survey-tool/components/pp-button';
import { useAppTranslation } from '../../i18n';
import { useAppNavigate } from '../../utils/browser-navigate';
import { isHorizonMaori } from '../../survey-tool/utils/panels';

interface LoginFormProps {
  isWidget: boolean;
}

interface Values {
  email: string;
  password: string;
  showPassword: boolean;
}

interface Errors {
  email: boolean;
  password: boolean;
  recaptcha: boolean;
}

const useStyles = makeStyles<any, PanelInfoAppTheme>({
  main: {
    display: 'flex',
    height: '100%',
    background: '#FAFAFA',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  container: {
    width: 380,
    '@media (max-width: 390px)': {
      width: 300,
    },
  },
  errorContainer: {
    width: '100%',
    marginTop: 2,
  },
  errorText: {
    marginLeft: 5,
    fontWeight: 600,
    fontSize: 12,
  },
  title: {
    color: '#333333',
    fontSize: 32,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  subtitle: {
    color: '#323232',
    fontSize: 16,
    letterSpacing: '0.23px',
    marginBottom: 32,
  },
  forgotLink: {
    fontSize: 12,
    width: '100%',
  },
  recaptcha: {
    marginTop: 20,
  },
  button: {
    width: '100%',
    background: '#fff',
    boxShadow: '0 2px 24px 0 rgba(0,0,0,0.5)',
    borderRadius: 6,
    padding: '10px 8px 10px',
    textAlign: 'center',
    color: ({ color_secondary }) => color_secondary,
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginTop: 50,
  },
});

export const LoginForm: FC<LoginFormProps> = ({ isWidget }) => {
  const { t, i18n } = useAppTranslation();
  const navigate = useAppNavigate();
  const panelInfo = useAppSelector((state) => state.panelInfo);
  const { recaptchaRequired, setRecaptchaRequired } = useRecaptchaRequired('login');
  const recaptchaRef = useRef<any>(null);

  const classes = useStyles(panelInfo?.app_theme);

  const [values, setValues] = useState<Values>({
    email: '',
    password: '',
    showPassword: false,
  });
  const [errors, setErrors] = useState<Errors>({
    email: false,
    password: false,
    recaptcha: false,
  });
  const [recaptchaResponse, setRecaptchaResponse] = useState<string | null>(null);

  const submitForm = useCallback(async () => {
    const newErrors: Errors = {
      email: false,
      password: false,
      recaptcha: false,
    };
    if (!values.email || !validateEmail(values.email)) {
      // setErrors({...errors, email: true});
      newErrors.email = true;
    }
    if (!values.password) {
      newErrors.password = true;
    }
    if (recaptchaRequired && !recaptchaResponse) {
      newErrors.recaptcha = true;
    }
    if (Object.values(newErrors).every((x) => x === false)) {
      try {
        const loginResponse = await callUserLogin({
          panelKey: panelInfo.guid,
          email: values.email,
          password: values.password,
          recaptchaKey: RECAPTCHA_SITE_NAME,
          recaptchaResponse,
        });
        if (loginResponse.ppToken) {
          storeToken(loginResponse.ppToken);
        }
        if (await checkAndUseQueryRedirect(loginResponse)) {
          return;
        }
        redirectIframeSafe(`/?token=${loginResponse.ppToken}`);
      } catch (err: any) {
        if (isClientErr(err) && err.data?.code === 'recaptcha-required') {
          setRecaptchaRequired(true);
        } else if (isClientErr(err) && err.data?.code === 'invalid_status' && err.data?.status === 2) {
          redirectIframeSafe('/suspended');
        } else {
          toast.error(err.message);
          setRecaptchaResponse(null);
        }
        recaptchaRef.current?.reset();
      }
    } else {
      setErrors(newErrors);
    }
  }, [panelInfo, values, recaptchaRef, recaptchaRequired, recaptchaResponse, setRecaptchaRequired]);

  useEffect(() => {
    const handleKeyDown = (ev: KeyboardEvent) => {
      if (ev.code === 'Enter' || ev.code === 'NumpadEnter') {
        if (values.email && values.password) {
          submitForm();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [submitForm, values]);

  const handleChange = (prop: keyof Values) => (event: ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [prop]: event.target.value.trim() });
    setErrors({ ...errors, [prop]: false });
  };

  const toggleShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };

  const onRecaptchaChange = (token: string | null) => {
    setRecaptchaResponse(token);
    setErrors({ ...errors, recaptcha: false });
  };

  const mainStyle: CSSProperties = {};
  if (isWidget) {
    mainStyle.background = 'none';
  }

  const helloText = isHorizonMaori(panelInfo) ? 'Kia Ora' : t('hello', 'Hello');
  const emailErrorText = t('pleaseEnterAValidEmailAddress', 'Please enter a valid email address');
  const passwordErrorText = t('pleasePutYourPassword', 'Please put your password');
  const recaptchaErrorText = t('pleaseFillTheRecaptcha', 'Please fill the recaptcha');

  return (
    <div className={classes.main} style={mainStyle}>
      <Box display="flex" flexDirection="column" alignItems="center" className={classes.container}>
        {!isWidget && (
          <>
            <Typography variant="h3" className={classes.title}>
              {helloText}
            </Typography>
            <Typography className={classes.subtitle}>{t('logInToEarnRewards', 'Log in to earn rewards!')}</Typography>
          </>
        )}

        <TextField
          error={!!errors.email}
          label={t('emailAddress', 'Email address')}
          autoComplete="email"
          style={{ width: '100%' }}
          value={values.email}
          onChange={handleChange('email')}
        />
        {errors.email && (
          <Grid className={classes.errorContainer} component="section" container alignItems="center">
            <ErrorIcon color="error" />
            <Typography className={classes.errorText} color="error">
              {emailErrorText}
            </Typography>
          </Grid>
        )}

        <TextField
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton aria-label="show-password" tabIndex={-1} onClick={toggleShowPassword} size="large">
                  <VisibilityIcon color="inherit" fontSize="inherit" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          error={!!errors.password}
          label={t('password', 'Password')}
          autoComplete="current-password"
          type={values.showPassword ? 'text' : 'password'}
          style={{ width: '100%' }}
          value={values.password}
          onChange={handleChange('password')}
        />
        {errors.password && (
          <Grid className={classes.errorContainer} component="section" container alignItems="center">
            <ErrorIcon color="error" />
            <Typography className={classes.errorText} color="error">
              {passwordErrorText}
            </Typography>
          </Grid>
        )}
        <Link className={classes.forgotLink} to="/forgot-password" tabIndex={-1}>
          {t('forgotten', 'Forgotten')} / {t('resetPassword', 'Reset password')}
        </Link>

        {recaptchaRequired && (
          <Grid component="section" className={classes.recaptcha} alignItems="center">
            <ReCAPTCHA
              hl={i18n.language}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={onRecaptchaChange}
              ref={recaptchaRef}
            />
            {errors.password && (
              <Grid className={classes.errorContainer} component="section" container alignItems="center">
                <ErrorIcon color="error" />
                <Typography className={classes.errorText} color="error">
                  {passwordErrorText}
                </Typography>
              </Grid>
            )}
            {errors.recaptcha && (
              <Grid className={classes.errorContainer} component="section" container alignItems="center">
                <ErrorIcon color="error" />
                <Typography className={classes.errorText} color="error">
                  {recaptchaErrorText}
                </Typography>
              </Grid>
            )}
          </Grid>
        )}

        <PPButton
          onClick={submitForm}
          sx={{
            width: '100%',
            height: 48,
            marginTop: '33px',
            borderRadius: 24,
            fontSize: 14,
            fontWeight: 'bold',
            letterSpacing: 1,
            textTransform: 'uppercase',
          }}
        >
          {t('logIn', 'Log in')}
        </PPButton>
        <PPButton
          onClick={() => navigate(isWidget ? '/widgets/join' : '/join')}
          color="secondary"
          sx={{
            borderRadius: 24,
            fontSize: 14,
            fontWeight: 'bold',
            height: 48,
            letterSpacing: 1,
            marginTop: '24px',
            textTransform: 'uppercase',
            width: '100%',
          }}
        >
          {t('dontHaveAnAccountQuestion', "Don't have an account?")} {t('signUp', 'Sign up')}
        </PPButton>
      </Box>
    </div>
  );
};
